import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPick, getTelegramMessage, markPickSent, updatePick } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function PickDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pick, setPick] = useState(null);
  const [telegramMessage, setTelegramMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [pickRes, msgRes] = await Promise.all([
        getPick(id),
        getTelegramMessage(id)
      ]);
      setPick(pickRes.data);
      setTelegramMessage(msgRes.data.message);
    } catch (error) {
      console.error('Error fetching pick:', error);
      toast.error('Error al cargar el pronóstico');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(telegramMessage);
      setCopied(true);
      toast.success('Mensaje copiado al portapapeles');
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error('Error al copiar');
    }
  };

  const handleMarkSent = async () => {
    try {
      await markPickSent(id);
      setPick(prev => ({ ...prev, sent_to_telegram: true }));
      toast.success('Marcado como enviado a Telegram');
    } catch (error) {
      toast.error('Error al marcar como enviado');
    }
  };

  const handleUpdateResult = async (result) => {
    try {
      await updatePick(id, { result });
      setPick(prev => ({ ...prev, result }));
      toast.success('Resultado actualizado');
    } catch (error) {
      toast.error('Error al actualizar resultado');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <Navbar />
        <main className="pt-24 px-4 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#00E599] animate-spin" />
        </main>
      </div>
    );
  }

  if (!pick) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <Navbar />
        <main className="pt-24 px-4 text-center">
          <p className="text-zinc-400">Pronóstico no encontrado</p>
        </main>
      </div>
    );
  }

  const leagueLabels = {
    la_liga: 'La Liga',
    premier_league: 'Premier League',
    bundesliga: 'Bundesliga',
    champions_league: 'Champions League',
    mls: 'MLS',
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      
      <main className="pt-20 pb-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back */}
          <button
            onClick={() => navigate(-1)}
            className="text-zinc-400 hover:text-white mb-6 transition-colors text-sm"
          >
            ← Volver
          </button>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Pick Info */}
            <div className="space-y-4">
              {/* Header Card */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
                <div className="bg-zinc-800/50 px-6 py-4 flex items-center justify-between">
                  <span className="text-sm text-zinc-400">
                    {leagueLabels[pick.league] || pick.league}
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge className={pick.channel === 'vip' 
                      ? 'bg-[#EAB308]/20 text-[#EAB308] border-0' 
                      : 'bg-zinc-700 text-zinc-300 border-0'
                    }>
                      {pick.channel === 'vip' ? 'VIP' : 'FREE'}
                    </Badge>
                    {pick.sent_to_telegram && (
                      <Badge className="bg-[#00E599]/20 text-[#00E599] border-0">
                        Enviado
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="p-6">
                  <h1 className="font-heading text-2xl font-bold text-white mb-2">
                    {pick.home_team} vs {pick.away_team}
                  </h1>
                  <p className="text-sm text-zinc-400 mb-6">
                    {new Date(pick.match_date).toLocaleDateString('es-ES', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-zinc-950 rounded-lg p-4">
                      <span className="text-xs text-zinc-500 block mb-1">{pick.market || 'Pronóstico'}</span>
                      <span className="font-heading font-bold text-white">{pick.prediction}</span>
                    </div>
                    <div className="bg-zinc-950 rounded-lg p-4">
                      <span className="text-xs text-zinc-500 block mb-1">Cuota</span>
                      <span className="font-mono text-xl font-bold text-[#00E599]">@{pick.odds?.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="bg-zinc-950 rounded-lg p-3 text-center">
                      <span className="text-xs text-zinc-500 block mb-1">Casa</span>
                      <span className="text-sm font-medium text-white">{pick.bookmaker}</span>
                    </div>
                    <div className="bg-zinc-950 rounded-lg p-3 text-center">
                      <span className="text-xs text-zinc-500 block mb-1">Stake</span>
                      <span className="text-sm font-medium text-white">{pick.stake}/10</span>
                    </div>
                    <div className="bg-zinc-950 rounded-lg p-3 text-center">
                      <span className="text-xs text-zinc-500 block mb-1">Confianza</span>
                      <span className={`text-sm font-medium ${
                        pick.confidence >= 75 ? 'text-[#00E599]' :
                        pick.confidence >= 60 ? 'text-[#EAB308]' : 'text-zinc-400'
                      }`}>{pick.confidence}%</span>
                    </div>
                  </div>

                  {/* Analysis */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-zinc-400 mb-2">Análisis:</h3>
                    <p className="text-zinc-300 text-sm leading-relaxed">{pick.analysis}</p>
                  </div>

                  {/* Value Explanation (if exists) */}
                  {pick.value_explanation && (
                    <div className="mb-6 bg-[#00E599]/10 border border-[#00E599]/20 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-[#00E599] mb-2">¿Por qué esta apuesta tiene valor?</h3>
                      <p className="text-zinc-300 text-sm">{pick.value_explanation}</p>
                    </div>
                  )}

                  {/* Result */}
                  <div>
                    <h3 className="text-sm font-medium text-zinc-400 mb-2">Resultado:</h3>
                    <Select
                      value={pick.result || "pending"}
                      onValueChange={(v) => handleUpdateResult(v === 'pending' ? null : v)}
                    >
                      <SelectTrigger className="w-full bg-zinc-950 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="pending">Pendiente</SelectItem>
                        <SelectItem value="won">Ganado</SelectItem>
                        <SelectItem value="lost">Perdido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Post-Analysis for Failed Picks */}
              {pick.result === 'lost' && (
                <div className="bg-[#F43F5E]/10 border border-[#F43F5E]/20 rounded-xl p-6">
                  <h3 className="font-heading text-lg font-bold text-[#F43F5E] mb-3">
                    Post-Análisis (Fallo)
                  </h3>
                  
                  {/* Match Result */}
                  {pick.match_result && (
                    <div className="bg-zinc-950 rounded-lg p-4 mb-4">
                      <span className="text-xs text-zinc-500 block mb-2">Resultado Real</span>
                      <div className="flex items-center justify-center gap-4">
                        <span className="text-white font-medium">{pick.home_team}</span>
                        <span className="text-2xl font-heading font-bold text-white">
                          {pick.match_result.home_goals} - {pick.match_result.away_goals}
                        </span>
                        <span className="text-white font-medium">{pick.away_team}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Failure Explanation */}
                  {pick.failure_explanation ? (
                    <div>
                      <h4 className="text-sm font-medium text-zinc-400 mb-2">Explicación del Modelo:</h4>
                      <p className="text-zinc-300 text-sm leading-relaxed">{pick.failure_explanation}</p>
                    </div>
                  ) : (
                    <p className="text-zinc-400 text-sm">
                      El análisis del fallo se generará automáticamente cuando el resultado sea verificado.
                    </p>
                  )}
                  
                  {/* Verified timestamp */}
                  {pick.verified_at && (
                    <p className="text-xs text-zinc-500 mt-4">
                      Verificado: {new Date(pick.verified_at).toLocaleString('es-ES')}
                    </p>
                  )}
                </div>
              )}

              {/* Success indicator for won picks */}
              {pick.result === 'won' && (
                <div className="bg-[#00E599]/10 border border-[#00E599]/20 rounded-xl p-6 text-center">
                  <span className="text-4xl mb-2 block">✅</span>
                  <h3 className="font-heading text-lg font-bold text-[#00E599]">¡Acierto!</h3>
                  {pick.match_result && (
                    <p className="text-zinc-400 mt-2">
                      Resultado: {pick.home_team} {pick.match_result.home_goals} - {pick.match_result.away_goals} {pick.away_team}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Telegram Message */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="bg-[#6366F1]/10 border-b border-zinc-800 px-6 py-4">
                <h2 className="font-heading text-lg font-bold text-white">
                  Mensaje para Telegram
                </h2>
                <p className="text-xs text-zinc-400 mt-1">
                  Copia este mensaje y envíalo al canal correspondiente
                </p>
              </div>

              <div className="p-6">
                <Textarea
                  value={telegramMessage}
                  onChange={(e) => setTelegramMessage(e.target.value)}
                  className="input-dark min-h-[300px] font-mono text-sm"
                  data-testid="telegram-message"
                />

                <div className="flex gap-3 mt-4">
                  <Button
                    onClick={handleCopyMessage}
                    className={`flex-1 ${copied ? 'bg-[#00E599] hover:bg-[#00E599]' : 'bg-zinc-800 hover:bg-zinc-700'}`}
                    data-testid="copy-message-btn"
                  >
                    {copied ? '¡Copiado!' : 'Copiar Mensaje'}
                  </Button>
                  
                  {!pick.sent_to_telegram && (
                    <Button
                      onClick={handleMarkSent}
                      className="btn-primary"
                      data-testid="mark-sent-btn"
                    >
                      Marcar Enviado
                    </Button>
                  )}
                </div>

                <div className="mt-4 p-4 bg-zinc-950 rounded-lg">
                  <p className="text-xs text-zinc-500">
                    Abre Telegram Web, selecciona el canal{' '}
                    <span className={pick.channel === 'vip' ? 'text-[#EAB308]' : 'text-[#00E599]'}>
                      {pick.channel === 'vip' ? 'VIP' : 'Gratuito'}
                    </span>{' '}
                    y pega el mensaje copiado.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
